import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from 'src/app/shared/services/workflow/workflow.service';
import { requiredFileListUnlessProvidedMedia } from '../workflow-file-upload-validators';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeWorkflowProvidedMediaComponent } from '../../payee-workflow-provided-media/payee-workflow-provided-media.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropzoneComponent } from '../../payee-dropzone/payee-dropzone.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';

type DialogData = {
  transition_label: string;
};

@Component({
  selector: 'app-info-notification-dialog',
  templateUrl: './info-notification-dialog.component.html',
  styleUrls: ['./info-notification-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeWorkflowProvidedMediaComponent,
    PayeeFormFieldComponent,
    PayeeDropzoneComponent,
    PayeeIconComponent,
    PayeeIconButtonComponent,
    PayeeButtonComponent,
  ],
})
export class InfoNotificationDialogComponent implements OnInit {
  readonly data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<
      MatDialogRef<InfoNotificationDialogComponent, RunTransition | undefined>
    >(MatDialogRef);
  private workflowService = inject(WorkflowService);
  private fb = inject(FormBuilder);

  loading = false;
  dialogData?: DialogData;

  readonly form = this.fb.group({
    files: this.fb.control<File[]>([], {
      validators: requiredFileListUnlessProvidedMedia(this.data.media),
      nonNullable: true,
    }),
  });

  get files() {
    return this.form.controls.files;
  }

  get dropzoneTitle(): string {
    if (!this.dialogData?.transition_label) {
      return '';
    }
    const parts = this.dialogData.transition_label.split('|');
    return parts.length > 1
      ? parts.slice(1).join('|').trim()
      : this.dialogData.transition_label;
  }

  async ngOnInit() {
    this.dialogData = (await this.workflowService.getDialogData({
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
    })) as DialogData;
  }

  onDrop(files: FileList) {
    const fileValues = [...this.files.value];
    for (let i = 0; i < files.length; ++i) {
      fileValues.push(files.item(i) as File);
    }
    this.files.setValue(fileValues);
  }

  removeFile(i: number) {
    const fileValues = [...this.files.value];
    fileValues.splice(i, 1);
    this.files.setValue(fileValues);
  }

  readonly onSubmit = async () => {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const { files } = this.form.getRawValue();

    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
      params: {
        ...(this.data.extraParams ?? {}),
        files: [...(this.data.media ?? []).map((m) => m.uuid), ...files],
      },
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };

  readonly onCancel = () => {
    this.dialogRef.close();
  };
}
